import { useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { Button } from "./ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useAppDispatch } from "@/redux/hooks";
import { updateUser } from "@/redux/user/user.action";
import { Input } from "./ui/input";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { DropdownMenuItem } from "./ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { RequestStatus } from "@/utilities/ResponseEnum/RequestStatus";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";

interface DialogProfileProps {
  userId: string;
}
const FormSchema = z
  .object({
    password: z.string().min(6, {
      message: "La contraseña debe tener al menos 6 caracteres.",
    }),
    confirmPassword: z.string().min(6, {
      message: "La confirmación debe tener al menos 6 caracteres.",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden.",
    path: ["confirmPassword"],
  });

const DialogSpecificPassword = ({ userId }: DialogProfileProps) => {
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useAppDispatch();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });
  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const response = await dispatch(
      updateUser({
        user: { password: data.password },
        userId: userId,
      }),
    );
    if (response.meta.requestStatus === RequestStatus.Fulfilled) {
      SuccessToast({ message: "Contraseña actualizada correctamente" });
      setOpen(false);
      form.reset();
    } else {
      ErrorToast({ message: "Error al actualizar la contraseña" });
      setOpen(false);
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
          }}
        >
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={400}>
            Cambiar contraseña
          </Typography>
        </DropdownMenuItem>
      </DialogTrigger>
      <VisuallyHidden>
        <DialogTitle>PasswordConfig</DialogTitle>
      </VisuallyHidden>
      <DialogContent className="bg-sidebar min-w-[15rem] w-[30rem]">
        <DialogHeader className="flex pb-0 h-fit">
          <Box className="flex justify-end w-full font-bold">
            <Button
              className="p-0 text-[#1F6B45] h-fit"
              onClick={() => setOpen(false)}
            >
              <IconX className="p-0" />
            </Button>
          </Box>
          <DialogTitle>
            <Typography
              fontFamily={"Inter"}
              fontSize={"18px"}
              fontWeight={500}
              letterSpacing={0}
            >
              Cambiar contraseña
            </Typography>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Cambia tu contraseña actual por una nueva para mantener tu cuenta
            segura.
          </DialogDescription>
        </DialogHeader>
        <Grid container gap={1} className="w-full flex justify-between">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full space-y-6 flex flex-col gap-4"
            >
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="max-h-[3.6rem]">
                    <FormLabel>Nueva contraseña</FormLabel>
                    <FormControl>
                      <Box
                        sx={{
                          minWidth: "100%",
                          maxWidth: "100%",
                          position: "relative",
                        }}
                      >
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Nueva contraseña"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="size-5 text-[#1F6B45]" />
                          ) : (
                            <Eye className="size-5 text-[#1F6B45]" />
                          )}
                        </button>
                      </Box>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="max-h-[3.6rem]">
                    <FormLabel>Confirmar contraseña</FormLabel>
                    <FormControl>
                      <div className="relative min-w-[100%] max-w-[100%]">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirmar contraseña"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="size-5 text-[#1F6B45]" />
                          ) : (
                            <Eye className="size-5 text-[#1F6B45]" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter
                className={`flex w-full gap-4 pt-4 flex-row items-center justify-center flex-wrap sm:flex-row sm:justify-end sm:items-center`}
              >
                <Button
                  variant="cancel"
                  className="w-full justify-self-start w-[7.5rem] flex"
                  onClick={() => {
                    form.reset();
                    setOpen(false);
                  }}
                >
                  <Typography
                    fontFamily={"Inter"}
                    fontSize={"14px"}
                    fontWeight={500}
                  >
                    Cancelar
                  </Typography>
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  className="w-full justify-self-end w-[7.5rem] flex hover:bg-[#1F6B45]/90"
                >
                  <Typography
                    fontFamily={"Inter"}
                    fontSize={"14px"}
                    fontWeight={500}
                  >
                    Confirmar
                  </Typography>
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default DialogSpecificPassword;
